import { useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { DetailDrawer } from "@/components/admin/ui";
import { APPOINTMENT_TYPE_LABELS } from "./officeMeta";

const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400";
const inputClass = "mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white";

export const JobFormModal = ({ meta, API, onClose, onCreated }) => {
  const availableTeams = meta.scope.level === "org" ? meta.teams : meta.teams.filter((t) => (meta.scope.team_ids || []).includes(t.team_id));
  const [teamId, setTeamId] = useState(availableTeams[0]?.team_id || "");
  const [assignedUserIds, setAssignedUserIds] = useState([]);
  const [appointmentType, setAppointmentType] = useState("service_job");
  const [title, setTitle] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [leadId, setLeadId] = useState("");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const teamMembers = meta.users.filter((u) => u.active && (u.team_ids || []).includes(teamId));

  const toggleUser = (userId) => setAssignedUserIds((ids) => ids.includes(userId) ? ids.filter((i) => i !== userId) : [...ids, userId]);

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/org/office/jobs`, {
        team_id: teamId,
        assigned_user_ids: assignedUserIds,
        lead_id: leadId || null,
        appointment_type: appointmentType,
        start_at: startAt,
        end_at: endAt,
        title: title || null,
        customer_name: customerName || null,
        notes: notes || null,
      }, { withCredentials: true });
      onCreated();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DetailDrawer open onClose={onClose} title="New Job" subtitle="Schedule a standalone Job, or link it to an existing Sales Lead." testId="office-job-create-drawer">
      <div className="space-y-4" data-testid="office-job-create-form">
        {error && <p className="text-sm font-semibold text-red-600 dark:text-red-400" data-testid="office-job-create-error">{error}</p>}

        <div>
          <label className={labelClass}>Team</label>
          <select value={teamId} onChange={(e) => { setTeamId(e.target.value); setAssignedUserIds([]); }} data-testid="office-job-create-team-select" className={inputClass}>
            <option value="">Select Team…</option>
            {availableTeams.map((t) => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
          </select>
        </div>

        <div>
          <label className={labelClass}>Appointment Type</label>
          <div className="mt-1 flex gap-2">
            {Object.entries(APPOINTMENT_TYPE_LABELS).map(([v, l]) => (
              <button key={v} type="button" onClick={() => setAppointmentType(v)} data-testid={`office-job-create-type-${v}`}
                className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${appointmentType === v ? "border-[#0052FF] bg-[#0052FF] text-white" : "border-slate-200 text-slate-600 dark:border-white/15 dark:text-slate-300"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Start</label>
            <input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} data-testid="office-job-create-start-input" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>End</label>
            <input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} data-testid="office-job-create-end-input" className={inputClass} />
          </div>
        </div>

        <div>
          <label className={labelClass}>Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Lawn Maintenance" data-testid="office-job-create-title-input" className={inputClass} />
        </div>

        <div>
          <label className={labelClass}>Customer Name (optional)</label>
          <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} data-testid="office-job-create-customer-input" className={inputClass} />
        </div>

        <div>
          <label className={labelClass}>Link to Sales Lead ID (optional)</label>
          <input value={leadId} onChange={(e) => setLeadId(e.target.value)} placeholder="e.g. lead_abc123" data-testid="office-job-create-lead-input" className={inputClass} />
        </div>

        <div>
          <label className={labelClass}>Assigned Team Members</label>
          <div className="mt-1 max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2 dark:border-white/15">
            {teamMembers.map((u) => (
              <label key={u.user_id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-white/5">
                <input type="checkbox" checked={assignedUserIds.includes(u.user_id)} onChange={() => toggleUser(u.user_id)} data-testid={`office-job-create-assignee-${u.user_id}`} />
                {u.name}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className={labelClass}>Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="office-job-create-notes-input" className={inputClass} />
        </div>

        <button type="button" onClick={submit} disabled={saving || !teamId || !startAt || !endAt} data-testid="office-job-create-submit-btn"
          className="w-full rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2.5 text-sm font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50">
          {saving ? "Scheduling…" : "Schedule Job"}
        </button>
      </div>
    </DetailDrawer>
  );
};
